import React from 'react';
import { Link } from 'react-router-dom';
import { Wifi, Shield, Network, Mail, Phone, MapPin, Tv } from 'lucide-react';
import { DiyaRow } from './diwali/Diyas';
import { isDiwaliPeriod } from './diwali/DiwaliTheme';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300 border-t-4" style={{ borderTopColor: '#ef4444' }}>
      {isDiwaliPeriod() && <DiyaRow count={9} size={40} />}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Contact Bar */}
        <div className="mb-8 p-6 rounded-xl border-4" style={{ 
          background: 'linear-gradient(135deg, #fef2f2 0%, #fefce8 50%, #fce7f3 100%)',
          borderColor: '#f59e0b'
        }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
              }}>
                <Phone className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600">24/7 Support</p>
                <a href="tel:+919319919988" data-testid="footer-support-mobile-link" className="text-2xl font-bold text-red-600 hover:text-red-700">
                  +91 93199 19988
                </a>
                <a href="tel:06276459015" data-testid="footer-office-phone-link" className="block text-sm font-semibold text-gray-700 hover:text-red-700">
                  Office Phone - 06276 - 459015
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-red-600" />
              <div>
                <p className="text-sm font-semibold text-gray-600">Our Offices</p>
                <p className="text-lg font-bold text-gray-800">Delhi · Haryana · Bihar · Uttar Pradesh</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <img 
              src="/netix-logo-main.png" 
              alt="Netix Fibernet" 
              className="h-12 mb-4 brightness-0 invert"
            />
            <p className="text-sm leading-relaxed">
              Leading provider of high-speed internet, CCTV cameras, and networking solutions across India.
            </p>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-bold mb-4 text-lg">Our Products</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/service/internet-leased-line" className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors">
                  <Network className="h-4 w-4" />
                  <span>Internet Leased Line</span>
                </Link>
              </li>
              <li>
                <Link to="/service/broadband-services" className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors">
                  <Wifi className="h-4 w-4" />
                  <span>Broadband Services</span>
                </Link>
              </li>
              <li>
                <Link to="/service/ott-services" className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors">
                  <Tv className="h-4 w-4" />
                  <span>OTT Services</span>
                </Link>
              </li>
              <li>
                <Link to="/service/voip-services" className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors">
                  <Phone className="h-4 w-4" />
                  <span>VoIP Services</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold mb-4 text-lg">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm hover:text-yellow-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/signup" className="text-sm hover:text-yellow-400 transition-colors">
                  Join Referral Program
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sm hover:text-yellow-400 transition-colors">
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-bold mb-4 text-lg">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-red-400" />
                <a href="tel:+919319919988" data-testid="footer-contact-mobile-link" className="hover:text-yellow-400 transition-colors font-semibold">
                  +91 93199 19988
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-red-400" />
                <a href="tel:06276459015" data-testid="footer-contact-office-link" className="hover:text-yellow-400 transition-colors font-semibold">
                  Office Phone - 06276 - 459015
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-red-400" />
                <a href="mailto:care@netixfibernet.com" className="hover:text-yellow-400 transition-colors">
                  care@netixfibernet.com
                </a>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-red-400 mt-0.5" />
                <div>
                  <p className="text-gray-300">Our Offices:</p>
                  <p className="text-white font-medium">Delhi · Haryana · Bihar · Uttar Pradesh</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications Strip */}
        <div className="border-t border-gray-800 pt-6 mb-8" data-testid="footer-certifications-strip">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-3" data-testid="footer-iso-certification">
              <img
                src="/badges/iso-9001-2015.png"
                alt="ISO 9001:2015 Certified - Quality Management System"
                className="h-20 w-20 object-contain flex-shrink-0"
                loading="lazy"
              />
              <div>
                <p className="text-base font-bold text-yellow-400">ISO 9001:2015</p>
                <p className="text-sm text-gray-400">Quality Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-testid="footer-iso-27001-certification">
              <img
                src="/badges/iso-27001-2022.png"
                alt="ISO/IEC 27001:2022 Certified - Information Security Management"
                className="h-20 w-20 object-contain flex-shrink-0"
                loading="lazy"
              />
              <div>
                <p className="text-base font-bold text-yellow-400">ISO/IEC 27001:2022</p>
                <p className="text-sm text-gray-400">Information Security Management</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-testid="footer-msme-certification">
              <img
                src="/badges/msme.png"
                alt="MSME Registered - Govt. of India"
                className="h-20 w-20 object-contain flex-shrink-0"
                loading="lazy"
              />
              <div>
                <p className="text-base font-bold text-yellow-400">MSME Registered</p>
                <p className="text-sm text-gray-400">Govt. of India</p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-testid="footer-mca-certification">
              <img
                src="/badges/mca-incorporation.png"
                alt="Certificate of Incorporation - Ministry of Corporate Affairs"
                className="h-20 w-20 object-contain flex-shrink-0"
                loading="lazy"
              />
              <div>
                <p className="text-base font-bold text-yellow-400">Certificate of Incorporation</p>
                <p className="text-sm text-gray-400">Ministry of Corporate Affairs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">
              © {new Date().getFullYear()} Netix Fibernet. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6">
              <Link to="/terms" className="text-sm hover:text-yellow-400 transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm hover:text-yellow-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/refund-return" className="text-sm hover:text-yellow-400 transition-colors">
                Refund & Return Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
